import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import { normalizeMerchantId } from '@src/services/merchant-id-parser'
import { FailedVendorSyncItem, ForceTransferVendorResult } from '@src/types/failed-vendor-sync'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'failed-vendors'

export const failedVendorsQueryKeys = {
  all: [SCOPE] as const,
  byCompany: (companyId: string) => [...failedVendorsQueryKeys.all, companyId] as const,
}

const getFailedVendors = async (companyId: string): Promise<FailedVendorSyncItem[]> => {
  try {
    const response = await apiClient.get<FailedVendorSyncItem[]>(
      `${encodeURIComponent(companyId)}/accounting/failed-vendors`,
    )
    return response.data ?? []
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const forceTransferVendors = async (companyId: string, merchantIds: string[]): Promise<ForceTransferVendorResult[]> => {
  const response = await apiClient.post<ForceTransferVendorResult[]>(
    `${encodeURIComponent(companyId)}/accounting/vendors/force-transfer`,
    { merchantIds },
  )
  return response.data
}

const clearFailedVendors = async (companyId: string): Promise<void> => {
  await apiClient.delete(`${encodeURIComponent(companyId)}/accounting/failed-vendors`)
}

export const useGetFailedVendors = (companyId: string) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: failedVendorsQueryKeys.byCompany(companyId),
    queryFn: () => getFailedVendors(companyId),
    enabled: !!companyId,
  })

  return { data, isLoading, error, refetch }
}

export function useForceTransferVendors(
  companyId: string,
): [MutationFunction<ForceTransferVendorResult[], string[]>, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: (merchantIds: string[]) => forceTransferVendors(companyId, merchantIds),
    onSuccess: (results) => {
      const synced = new Set(
        results.filter((result) => result.success).map((result) => normalizeMerchantId(result.merchantId)),
      )
      if (synced.size === 0) return

      queryClient.setQueryData<FailedVendorSyncItem[]>(failedVendorsQueryKeys.byCompany(companyId), (current) =>
        current?.filter((vendor) => !synced.has(normalizeMerchantId(vendor.merchantId))),
      )
    },
    onError: (error) => {
      reportErrorToConsole(error)
    },
  })

  return [mutateAsync, isPending, reset]
}

export function useClearFailedVendors(companyId: string): [MutationFunction<void, void>, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: () => clearFailedVendors(companyId),
    onSuccess: () => {
      queryClient.setQueryData<FailedVendorSyncItem[]>(failedVendorsQueryKeys.byCompany(companyId), [])
    },
    onError: (error) => {
      reportErrorToConsole(error)
    },
  })

  return [mutateAsync, isPending, reset]
}
